import { COVER_IMAGE_HEIGHT, COVER_IMAGE_WIDTH } from 'app-constants/profileAndCoverImagesSize';
import { useModalProvider } from 'context/ModalProviderContext';
import { collection, doc, DocumentData, setDoc } from 'firebase/firestore';
import { ref } from 'firebase/storage';
import { ConfirmationModal } from 'modals/ConfirmationModal';
import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useWatch } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { loadImageOnCanvas } from 'services/utility/image/resizeImage';
import { Status } from 'shared/enums';
import styled, { useTheme } from 'styled-components';
import { Artist } from 'types';

import { COUNTRIES } from '../../../../app-constants/countries';
import { User } from '../../../../assets/LoginPage';
import { DeleteIcon } from '../../../../assets/Modal';
import { MODAL_TYPES, useGlobalModalContext } from '../../../../context/GlobalModal';
import { useForm } from '../../../../hooks';
import {
  artisteRejectedNotificationApi,
  checkFieldExist,
  disableUser,
  getUser,
  uploadArtistImage,
} from '../../../../services';
import { deleteFile } from '../../../../services/api/storageApi';
import {
  DEFAULT_COVER_IMAGE,
  DEFAULT_PROFILE_IMAGE,
  firestore,
  storage,
  STORAGE_URL,
} from '../../../../services/Firebase';
import { Button, Input, RejectionModal, SelectForm, Splash } from '../../../../shared/Components';
import {
  InformationModal,
  InformationModalTypes,
} from '../../../../shared/Components/InformationModal';
import UploadImage from '../../../../shared/Components/UploadImage/UploadImage';
import {
  AbsoluteImage,
  Box,
  DropZoneContainer,
  FlexContainer,
  Image,
  Text,
} from '../../../../shared/Typhography';
import { strings } from '../../../../strings';
import { UpdateField, updateSchema } from '../../../../validations';

const checkImageResolution = async (url: string, maxWidth: number, maxHeight: number) => {
  const img = await loadImageOnCanvas(url);

  return !(img.width < maxWidth || img.height < maxHeight);
};

interface FormInputs {
  [UpdateField.Name]: string;
  [UpdateField.RevenueShare]: number;
  [UpdateField.City]: string;
  [UpdateField.Country]: any;
  [UpdateField.Description]: string;
  [UpdateField.DisplayName]: string;
  [UpdateField.ProfileLink]: string;
  [UpdateField.PortfolioLink]: string;
  [UpdateField.Status]: any;
  [UpdateField.Reason]: any;
  [UpdateField.Suspended]: boolean;
  [UpdateField.RejectMessage]: string;
}

const FormContainerEditAccount = styled.form`
  display: flex;
  flex-direction: column;
  gap: 40px;
  margin-top: 50px;
`;

interface ParamTypes {
  id: string;
}

type StateSetterFunction<T> = Dispatch<SetStateAction<T>>;

const statusDropdownOptions = [
  { label: 'Pending', value: 'pending' },
  { label: 'Approved', value: 'approved' },
  { label: 'Rejected', value: 'rejected' },
  { label: 'Disabled', value: 'disabled' },
];

export default function EditArtistScreen() {
  const { id } = useParams<ParamTypes>();
  const { openModal, closeModal } = useModalProvider();
  const { showModal } = useGlobalModalContext();
  const queryClient = useQueryClient();

  const { data: artist, isLoading: isLoadingArtist } = getUser(id);
  const countriesOptions = COUNTRIES.map((item) => ({ label: item, value: item }));

  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [profileImagePreviewSrc, setProfileImagePreviewSrc] = useState<string | null>(null);
  const { getRootProps, isFocused } = useDropzone({
    onDrop: (files) => getImage(files, setProfileImage, setProfileImagePreviewSrc),
    maxSize: 10480000,
  });

  // State that allows tracking progress form submission.
  // Currently used for showing loading indicator on submit button,
  // so user has feedback of what is going on.
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [coverImagePreviewSrc, setCoverImagePreviewSrc] = useState<string | null>(null);

  const { colors } = useTheme();

  const { getRootProps: coverProps, isFocused: coverFocused } = useDropzone({
    onDrop: (files) => {
      getImage(files, setCoverImage, setCoverImagePreviewSrc);
    },
    maxSize: 10480000,
  });

  const schema = updateSchema(id, artist?.data() as Artist, {
    removeDescriptionLengthValidators: true,
    removeArtistDisplayNameLengthValidators: true,
  });
  const {
    handleSubmit,
    control,
    formState: { errors, isValid: isFormValid },
    setError,
    setValue,
  } = useForm<FormInputs>({ schema });

  const artistStatus = useWatch({ name: UpdateField.Status, control });
  const artistRejectMessage = useWatch({ name: UpdateField.RejectMessage, control });

  const isNotEmptyErrors = useMemo(() => Object.keys(errors).length > 0, [errors]);

  const getImage = async (
    files: File[],
    setFileFunction: StateSetterFunction<File | null>,
    setSrcPreviewFunction: StateSetterFunction<string | null>,
  ) => {
    const file = files[0];
    const url = URL.createObjectURL(file);

    setFileFunction(file);
    setSrcPreviewFunction(url);
  };

  const submitImageOnUpload = async (image: File, type: 'profile' | 'cover') => {
    try {
      if (image && artist) {
        const [profileImageUploadResult] = await uploadArtistImage(artist.id, image, type);
        const path = profileImageUploadResult.ref.fullPath;

        return `${STORAGE_URL}/${path}?timestamp=${new Date().getTime()}`;
      }
    } catch (error) {
      console.log(error);
      toast.error(strings.toastNotificationSomethingWrong);
      throw new Error('Error uploading images');
    }
  };

  const resetImageState = (
    setFileFunction: StateSetterFunction<any>,
    setSrcPreviewFunction: StateSetterFunction<any>,
  ) => {
    setFileFunction(null);
    setSrcPreviewFunction(null);
  };

  const onBeforeSubmit = useCallback(
    (callback) => {
      return (data: FormInputs) => {
        if (data.suspended === (artist?.data()?.flags || []).includes('suspended')) {
          callback(data);
          return true;
        }

        showModal(MODAL_TYPES.CONFIRMATION_MODAL, {
          title: strings.suspended.confirmTitle,
          content: () => {
            return (
              <div style={{ margin: '50px' }}>
                <Text textAlign="center">
                  {data.suspended
                    ? strings.suspended.confirmSuspending
                    : strings.suspended.confirmUnsuspending}
                </Text>
              </div>
            );
          },
          action: () => {
            callback(data);
            return true;
          },
        });
      };
    },
    [artist],
  );

  const onSubmit = handleSubmit(
    onBeforeSubmit(async (data: FormInputs) => {
      setIsLoading(true);
      const refUsers = collection(firestore, 'usersWeb');
      if (artist?.data()?.username != data.DisplayName) {
        const check = await checkFieldExist(refUsers, 'username', data.DisplayName);

        if (check) {
          setError(UpdateField.DisplayName, {
            type: 'custom',
            message: strings.usernameExist,
          });

          setIsLoading(false);
          return;
        }
      }

      if (artist?.data()?.profileLink != data.profileLink) {
        const check = await checkFieldExist(refUsers, 'profileLink', data.profileLink);

        if (check) {
          setError(UpdateField.ProfileLink, {
            type: 'custom',
            message: 'Profile Link is already taken',
          });

          setIsLoading(false);
          return;
        }
      }

      const previousProfileUrl = artist?.data()?.profileImageUrl;
      const previousCoverUrl = artist?.data()?.coverImageUrl;

      const flags = data.suspended ? ['suspended'] : [];

      const userData: DocumentData = {
        Name: data.name?.trim(),
        revenueShare: data.revenueShare / 100,
        City: data.city,
        Country: data.country.value,
        username: data.DisplayName?.trim(),
        Description: data.description,
        profileLink: data.profileLink,
        portfolioUrl: data.portfolioUrl,
        status: data.status.value,
        flags,
      };

      if (data.rejectMessage && data?.status?.value === Status.Rejected) {
        userData.rejectMessage = data.rejectMessage;
      } else {
        userData.rejectMessage = '';
      }

      const isNewProfileImage = profileImage && profileImagePreviewSrc !== DEFAULT_PROFILE_IMAGE;
      const shouldRevertToDefaultProfile = !profileImagePreviewSrc;

      if (isNewProfileImage) {
        userData.profileImageUrl = await submitImageOnUpload(profileImage, 'profile');
      } else if (shouldRevertToDefaultProfile) {
        userData.profileImageUrl = DEFAULT_PROFILE_IMAGE;
      }

      const isNewCoverImage = coverImage && coverImagePreviewSrc !== DEFAULT_PROFILE_IMAGE;
      const shouldRevertToDefaultCover = !coverImagePreviewSrc;

      if (isNewCoverImage) {
        const isValid = await checkImageResolution(
          coverImagePreviewSrc ?? '',
          COVER_IMAGE_WIDTH,
          COVER_IMAGE_HEIGHT,
        );

        if (!isValid) {
          setIsLoading(false);
          toast.error(
            `Image resolution must be at least ${COVER_IMAGE_WIDTH}x${COVER_IMAGE_HEIGHT}!`,
          );
          return;
        }

        userData.coverImageUrl = await submitImageOnUpload(coverImage, 'cover');
      } else if (shouldRevertToDefaultCover) {
        userData.coverImageUrl = DEFAULT_COVER_IMAGE;
      }

      // Reason must be provided only if status has changed to "disabled".
      if (data.status.value === 'disabled' && artist?.data()?.status !== 'disabled') {
        if (!data.reason || data.reason.length === 0) {
          toast.error('Reason must be provided when disabling artist.');
          setIsLoading(false);
          return;
        }
      }

      await setDoc(doc(refUsers, artist?.id), userData, { merge: true });

      await (async () => {
        await queryClient.invalidateQueries(['users', artist?.id]);
        await queryClient.invalidateQueries(['artists']);

        // imagePreviewSrc can be falsy when null
        const shouldDeletePreviousProfileImages =
          !profileImagePreviewSrc && previousProfileUrl !== DEFAULT_PROFILE_IMAGE;
        const shouldDeletePreviousCoverImages =
          !coverImagePreviewSrc && previousCoverUrl !== DEFAULT_COVER_IMAGE;

        // For batching delete operations.
        const promises = [];

        if (shouldDeletePreviousProfileImages) {
          promises.push(deleteFile(ref(storage, `/users/${artist?.id}/profile`)));
          promises.push(deleteFile(ref(storage, `/users/${artist?.id}/original_profile`)));
        }

        if (shouldDeletePreviousCoverImages) {
          promises.push(deleteFile(ref(storage, `/users/${artist?.id}/cover`)));
          promises.push(deleteFile(ref(storage, `/users/${artist?.id}/original_cover`)));
        }

        // Execute batched promises.
        Promise.all(promises)
          .then(() => {
            setIsLoading(false);
            toast.success(strings.toastNotificationSuccessEditAcc);
          })
          .catch((error) => {
            console.log('error here');
            setIsLoading(false);
            console.error(error);
            throw new Error(error);
          });
      })();

      if (data?.status?.value === Status.Rejected && userData.rejectMessage) {
        try {
          await artisteRejectedNotificationApi(id, userData.rejectMessage);
        } catch (err) {
          console.log(err);
          toast.error('Failed to send rejected notification');
        }
      }

      if (data.status.value === 'disabled' && artist) {
        await disableUser({ artistId: artist.id, reason: data.reason });
      }
    }),
  );

  const profileImageUrl: string = artist?.data()?.profileImageUrl || '';
  const coverImageUrl: string = artist?.data()?.coverImageUrl || '';

  const setStatusOnLoad = () => {
    if (!artist) return;

    const artistStatus: string = artist?.data()?.status;
    const rejectMessage: string = artist?.data()?.rejectMessage;

    setValue(UpdateField.Status, {
      value: artistStatus,
      label: `${artistStatus.slice(0, 1).toUpperCase()}${artistStatus.slice(1)}`,
    });

    if (rejectMessage) {
      setValue(UpdateField.RejectMessage, rejectMessage);
    }
  };

  useEffect(() => {
    if (isLoading) return;

    setStatusOnLoad();
  }, [artist]);

  useEffect(() => {
    if (profileImageUrl) {
      setProfileImagePreviewSrc(profileImageUrl);
    }
  }, [profileImageUrl]);

  useEffect(() => {
    if (coverImageUrl) {
      setCoverImagePreviewSrc(coverImageUrl);
    }
  }, [coverImageUrl]);

  useEffect(() => {
    if (!coverImagePreviewSrc) return;

    (async () => {
      const isValid = await checkImageResolution(
        coverImagePreviewSrc,
        COVER_IMAGE_WIDTH,
        COVER_IMAGE_HEIGHT,
      );

      if (!isValid)
        toast.error(
          `Image resolution must be at least ${COVER_IMAGE_WIDTH}x${COVER_IMAGE_HEIGHT}!`,
        );
    })();
  }, [coverImagePreviewSrc]);

  if (isLoadingArtist) return <Splash />;

  return (
    <>
      <Box margin="150px auto 50px auto">
        <Text size={2} weight="bold">
          {strings.editArtistTitle}
        </Text>
        <FlexContainer flexDirection="column" margin="1rem 0 0 0">
          <FlexContainer flexWrap="wrap" justifyContent="space-around" gap="15px" padding="15px">
            <FlexContainer
              flexDirection="column"
              alignItems="center"
              width="250px"
              style={{ position: 'relative' }}
            >
              {!profileImagePreviewSrc ? (
                <DropZoneContainer {...getRootProps({ isFocused })}>
                  <UploadImage buttonText={strings.uploadProfileImage} />
                </DropZoneContainer>
              ) : (
                <>
                  <AbsoluteImage
                    top={0}
                    right={0}
                    left={0}
                    bottom={0}
                    cursor="pointer"
                    src={DeleteIcon}
                    alt="remove image"
                    onClick={() => resetImageState(setProfileImage, setProfileImagePreviewSrc)}
                  />
                  <Image
                    height="160px"
                    width="160px"
                    style={{
                      objectFit: 'cover',
                      objectPosition: 'center',
                    }}
                    borderRadiusType="circle"
                    src={profileImagePreviewSrc}
                  />
                </>
              )}
            </FlexContainer>
            <FlexContainer
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              flex="1"
              style={{ position: 'relative' }}
            >
              {!coverImagePreviewSrc ? (
                <DropZoneContainer {...coverProps({ coverFocused })}>
                  <UploadImage buttonText={strings.uploadCoverImage} />
                </DropZoneContainer>
              ) : (
                <>
                  <AbsoluteImage
                    top={0}
                    right={0}
                    left={0}
                    bottom={0}
                    cursor="pointer"
                    src={DeleteIcon}
                    alt="remove image"
                    onClick={() => resetImageState(setCoverImage, setCoverImagePreviewSrc)}
                  />
                  <Image
                    borderRadiusType="rounded"
                    src={coverImagePreviewSrc}
                    style={{
                      objectFit: 'cover',
                      objectPosition: 'center',
                    }}
                    width="90%"
                    height="190px"
                  />
                </>
              )}
            </FlexContainer>
          </FlexContainer>
          <FormContainerEditAccount onSubmit={onSubmit}>
            <FlexContainer flexWrap="wrap" justifyContent="space-around">
              <Input
                required
                placeholder={strings.createAccNamePlaceholder}
                name={UpdateField.Name}
                type="text"
                control={control}
                error={errors[UpdateField.Name]}
                placeholderIcon={User}
                width="400px"
                defaultValue={artist?.data()?.Name}
              />
              <Input
                required
                placeholder={strings.displayName}
                name={UpdateField.DisplayName}
                type="text"
                control={control}
                error={errors[UpdateField.DisplayName]}
                width="400px"
                marginBottom={20}
                autoComplete={false}
                defaultValue={artist?.data()?.username}
              />
            </FlexContainer>
            <FlexContainer flexWrap="wrap" justifyContent="space-around">
              <SelectForm
                required
                placeholder={strings.selectCountry}
                name={UpdateField.Country}
                control={control}
                values={countriesOptions}
                error={errors[UpdateField.Country]}
                width="400px"
                defaultValue={
                  artist?.data()?.Country
                    ? { label: artist?.data()?.Country, value: artist?.data()?.Country }
                    : null
                }
              />
              <Input
                required
                placeholder={strings.city}
                name={UpdateField.City}
                type="text"
                control={control}
                error={errors[UpdateField.City]}
                width="400px"
                marginBottom={20}
                autoComplete={false}
                defaultValue={artist?.data()?.City}
              />
            </FlexContainer>
            <FlexContainer flexWrap="wrap" justifyContent="space-around">
              <Input
                required
                placeholder="Enter Profile Link"
                name={UpdateField.ProfileLink}
                type="text"
                control={control}
                error={errors[UpdateField.ProfileLink]}
                width="400px"
                marginBottom={20}
                autoComplete={false}
                defaultValue={artist?.data()?.profileLink}
              />
              <Input
                required
                placeholder={strings.revenueShare}
                name={UpdateField.RevenueShare}
                type="number"
                control={control}
                error={errors[UpdateField.RevenueShare]}
                width="400px"
                autoComplete={false}
                defaultValue={Math.floor(artist?.data()?.revenueShare * 100)}
                wrapperHeight="50px"
                interval={{
                  min: 0,
                  max: 100,
                }}
              />
            </FlexContainer>
            <FlexContainer flexWrap="wrap" justifyContent="space-around">
              <Input
                required
                placeholder="Enter Portfolio Link"
                name={UpdateField.PortfolioLink}
                type="text"
                control={control}
                error={errors[UpdateField.PortfolioLink]}
                width="400px"
                marginBottom={20}
                autoComplete={false}
                defaultValue={artist?.data()?.portfolioUrl}
              />
              <FlexContainer width="400px" height="20px" justifyContent="space-around" />
            </FlexContainer>
            <FlexContainer flexWrap="wrap" justifyContent="space-around">
              <FlexContainer gap="40px" flexDirection="column">
                <FlexContainer>
                  <SelectForm
                    required
                    id="status"
                    label="Status"
                    placeholder="Select artist status"
                    name={UpdateField.Status}
                    control={control}
                    values={statusDropdownOptions}
                    onChange={(value) => {
                      const status = value?.value;
                      const isApprovalRevoked =
                        artist?.data()?.status === Status.Approved && status !== Status.Approved;

                      setValue(UpdateField.RejectMessage, '');

                      if (isApprovalRevoked) {
                        openModal('revoked-approval-modal', ConfirmationModal as React.FC, {
                          title: <h1>Revoke approval?</h1>,
                          content: (
                            <>
                              You are trying to <b>revoke approval</b> for currently approved
                              creator, this will disable all his content. Are you sure you want do
                              it?
                            </>
                          ),
                          close: () => {
                            // Revert status to whatever it was in db.
                            setValue(
                              UpdateField.Status,
                              statusDropdownOptions?.find(
                                (item) => item?.value === artist?.data()?.status,
                              ),
                            );

                            closeModal('revoked-approval-modal');
                          },
                          cancel: () => {
                            // Revert status to whatever it was in db.
                            setValue(
                              UpdateField.Status,
                              statusDropdownOptions?.find(
                                (item) => item?.value === artist?.data()?.status,
                              ),
                            );

                            closeModal('revoked-approval-modal');
                          },
                          confirm: () => {
                            closeModal('revoked-approval-modal');
                          },
                          cancelButtonVariant: 'link',
                          confirmButtonVariant: 'danger',
                        });
                      }

                      if (status === Status.Rejected) {
                        openModal('reject-artist-modal-message', RejectionModal as React.FC, {
                          rejectionModalType: 'artist',
                          onClose: () => closeModal('reject-artist-modal-message'),
                          onSubmit: (message: string) =>
                            setValue(UpdateField.RejectMessage, message),
                        });
                      }
                    }}
                    error={errors[UpdateField.Status]}
                    width={artistStatus?.value === 'rejected' ? '380px' : '400px'}
                    defaultValue={statusDropdownOptions?.find(
                      (item) => item?.value === (artist?.data()?.status || 'approved'),
                    )}
                  />
                  {artistStatus?.value === 'rejected' && artistRejectMessage && (
                    <InformationModal
                      type={InformationModalTypes.Error}
                      title="Rejection reason"
                      content={artistRejectMessage}
                    />
                  )}
                </FlexContainer>

                {artistStatus?.value === 'disabled' && artist?.data()?.status !== 'disabled' && (
                  <Input
                    required
                    placeholder="Type in reason that will be included in artist notification email here..."
                    name={UpdateField.Reason}
                    type="textarea"
                    control={control}
                    error={errors[UpdateField.Reason]}
                    width="400px"
                    height="200px"
                    autoComplete={false}
                  />
                )}

                <FlexContainer
                  gap="10px"
                  alignItems="center"
                  justifyContent="center"
                  flexDirection="row"
                >
                  <Input
                    control={control}
                    type="checkbox"
                    width="25px"
                    defaultChecked={(artist?.data()?.flags || []).includes('suspended')}
                    defaultValue={(artist?.data()?.flags || []).includes('suspended')}
                    name={UpdateField.Suspended}
                    outerWidth="50%"
                    maxOuterWidth="25px"
                  />
                  <Text size={0.8} textAlign="left">
                    Suspended
                  </Text>
                </FlexContainer>
              </FlexContainer>

              <Input
                required
                placeholder={strings.description}
                name={UpdateField.Description}
                type="textarea"
                control={control}
                error={errors[UpdateField.Description]}
                width="400px"
                height="200px"
                autoComplete={false}
                defaultValue={artist?.data()?.Description || artist?.data()?.description}
              />
            </FlexContainer>

            <FlexContainer justifyContent="space-around">
              <Button
                width={400}
                height={58}
                type="submit"
                disabled={isLoading || (!isFormValid && isNotEmptyErrors)}
              >
                {isLoading ? (
                  <Splash width={35} height={35} color={colors.white} />
                ) : (
                  strings.submit
                )}
              </Button>
            </FlexContainer>
          </FormContainerEditAccount>
        </FlexContainer>
      </Box>
    </>
  );
}
